import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass } from "@phosphor-icons/react";

const PRIORITY_CLS = {
  low: "bg-slate-100 text-slate-700", medium: "bg-blue-50 text-blue-800",
  high: "bg-amber-50 text-amber-800", urgent: "bg-red-50 text-red-800",
};
const STATUS_CLS = {
  open: "bg-slate-100 text-slate-700", in_progress: "bg-blue-50 text-blue-800",
  done: "bg-green-50 text-green-800", blocked: "bg-red-50 text-red-800",
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [matters, setMatters] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    (async () => {
      const [t, m, u] = await Promise.all([api.get("/tasks"), api.get("/matters"), api.get("/users")]);
      setTasks(t.data); setMatters(m.data); setUsers(u.data);
    })();
  }, []);

  const matterMap = useMemo(() => Object.fromEntries(matters.map((m) => [m.id, m])), [matters]);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const filtered = tasks.filter((t) => {
    const s = q.toLowerCase();
    const mQ = !s || [t.title, matterMap[t.matter_id]?.title, matterMap[t.matter_id]?.reference].some((v) => (v || "").toLowerCase().includes(s));
    const mS = status === "all" || t.status === status;
    return mQ && mS;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Workstreams</div>
          <h2 className="font-serif-brand text-3xl">Tasks</h2>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search tasks…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm h-9 w-64 border-slate-300" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 rounded-sm h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-sm bg-white">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-6 py-3 font-medium">Task</th>
              <th className="text-left px-6 py-3 font-medium">Matter</th>
              <th className="text-left px-6 py-3 font-medium">Assigned</th>
              <th className="text-left px-6 py-3 font-medium">Priority</th>
              <th className="text-left px-6 py-3 font-medium">Status</th>
              <th className="text-left px-6 py-3 font-medium">Due</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filtered.length === 0 && <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-500">No tasks.</td></tr>}
            {filtered.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50">
                <td className="px-6 py-3">
                  <div className="font-medium">{t.title}</div>
                  {t.description && <div className="text-xs text-slate-500">{t.description}</div>}
                </td>
                <td className="px-6 py-3">
                  {matterMap[t.matter_id] ? (
                    <Link to={`/matters/${t.matter_id}`} className="hover:underline">
                      <div className="font-mono-num text-xs text-slate-500">{matterMap[t.matter_id].reference}</div>
                      <div className="text-slate-600">{matterMap[t.matter_id].title}</div>
                    </Link>
                  ) : "—"}
                </td>
                <td className="px-6 py-3 text-slate-600">{userMap[t.assigned_to]?.name || "—"}</td>
                <td className="px-6 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm ${PRIORITY_CLS[t.priority]}`}>{t.priority}</span></td>
                <td className="px-6 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_CLS[t.status]}`}>{t.status.replace("_", " ")}</span></td>
                <td className="px-6 py-3 text-slate-600">{formatDate(t.due_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
